import { IMetric } from "@gtmhub/okrs/metrics";
import { StateService } from "@uirouter/angularjs";
import { IWindowService } from "angular";
import { ItemAction, ItemActionHandler, ItemActionHref, ItemActionState } from "../components";
import { ISearchField } from "../services/searchService";

export function isIEBrowser(): boolean {
  return navigator.userAgent.indexOf("MSIE ") > -1 || navigator.userAgent.indexOf("Trident") > -1; // only IE property
}

export function isNumberRounded(number: number): boolean {
  return number % 1 === 0;
}

export function toFixedWithoutRounding(number: number | string, size = 3): number {
  const regex = new RegExp("^-?\\d+(?:\\.\\d{0," + size + "})?", "g");
  const stringifiedNum = number.toString().match(regex)[0];
  const dot = stringifiedNum.indexOf(".");
  if (dot === -1) {
    return +(stringifiedNum + "." + "0".repeat(size));
  }
  const num = size - (stringifiedNum.length - dot) + 1;
  return num > 0 ? +(stringifiedNum + "0".repeat(num)) : +stringifiedNum;
}

type IMarkedHrefOptions = { hrefLink: string; target: string; text: string; title: string; plaintext: boolean };

export function getHrefLinkOptions(link: string): IMarkedHrefOptions {
  const array = link.split(":::");
  const hrefLink = array[0];
  const target = getMarkedTabTarget(array.find((element) => element.includes("target=")));
  const text = getMarkedText(array.find((element) => element.includes("text=")));
  const title = getMarkedTitle(array.find((element) => element.includes("title=")));
  const plaintext = getMarkedPlaintext(array.find((element) => element.includes("plaintext=")));

  return {
    hrefLink,
    target,
    text,
    title,
    plaintext,
  };
}

export function getMarkedHrefLink(value: string): string {
  const mail = value && value.split("mailto:")[1];
  return mail || value || "";
}

export function getMarkedTitle(value: string): string {
  return value ? value.split("title=")[1] : "";
}

export function getMarkedPlaintext(value: string): boolean {
  return value ? value.split("plaintext=")[1] === "true" : false;
}

export function getMarkedText(value: string): string {
  return value ? value.split("text=")[1] : "";
}

export function getMarkedTabTarget(value: string): string {
  return value ? value.split("target=")[1] : "_self";
}

export function getMarkedLinkElement(options: IMarkedHrefOptions): string {
  if (options.plaintext) {
    return options.hrefLink;
  }

  return `<a href=${options.hrefLink} target=${options.target || "_self"} title=${options.title || ""}>${getMarkedHrefLink(options.text || options.hrefLink)}</a>`;
}

export function displayCriticalTarget(kr: IMetric): boolean {
  // the conditions for targetOperators may be needed
  // because when changing the targetOperator of anti-target metrics with critical targets patch method may has kept their critical value
  // kr critical could potentially be 0, so check explicitly for null or undefined
  if (typeof kr.critical === "undefined" || kr.critical === null || kr.targetOperator === "at_least" || kr.targetOperator === "at_most") {
    return false;
  }

  return true;
}

export function actionHandlerGenerator(action: ItemAction, dependancies: { $state: StateService; $window: IWindowService }): () => unknown {
  if (!action) {
    return angular.noop;
  }

  const { handler } = action as ItemActionHandler;
  if (handler) {
    return handler;
  }

  const { href, target } = action as ItemActionHref;
  if (href) {
    if (target) {
      return () => dependancies.$window.open(href, target);
    }

    return () => (dependancies.$window.location.href = href);
  }

  const { state, params, options } = action as ItemActionState;
  if (state) {
    return () => dependancies.$state.go(state, params, options);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toCsv = <Row extends Record<string, any>>(rows: Row[], header?: (keyof Row)[]): string => {
  if (!header && !rows.length) {
    return "";
  }

  if (!header) {
    header = Object.keys(rows[0]);
  }

  const jsonReplacer = (prop: string, value: string) => (typeof value === "undefined" || value == null ? "N/A" : value);
  return [header.join(","), ...rows.map((row) => header.map((fieldName) => JSON.stringify(row[fieldName], jsonReplacer)).join(","))].join("\r\n");
};

export function addItemAndReturnCollection<T>(items: Array<T>, item: T): T[] {
  items.push(item);
  return items;
}

export function utf8StringSize(str: string): number {
  return new Blob([str]).size;
}

export function utf8StringTruncateToSize(str: string, size: number): string {
  if (utf8StringSize(str) <= size) {
    return str;
  }

  let result = "";
  let resultSize = 0;
  const symbols = [...str];
  for (const symbol of symbols) {
    resultSize += utf8StringSize(symbol);
    if (resultSize > size) {
      break;
    }
    result += symbol;
  }
  return result;
}

/** copied from smartbanner.js user-agent-detector https://github.com/ain/smartbanner.js/blob/main/src/detector.js */
export function isMobile(): boolean {
  const maxTouchPoints = window.navigator.maxTouchPoints;
  const userAgent = window.navigator.userAgent;
  const isAndroid = /Android/i.test(userAgent);

  // maxTouchPoints is the only effective method to detect iPad iOS 13+
  const isIos = (maxTouchPoints && maxTouchPoints > 0) || /iPhone|iPad|iPod/i.test(userAgent);

  if (isAndroid || isIos) {
    return true;
  }

  return false;
}

export function generateSearchFields(searchFieldsNames: string[]): ISearchField[] {
  return searchFieldsNames.map((name) => ({ name: name }));
}
